import { ChevronLeftIcon, ChevronRightIcon } from '../icons/Icons';

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

/** Builds a compact page list such as [1, 2, '…', 7, 8]. */
function pageWindow(page: number, totalPages: number): (number | 'gap')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages, page, page - 1, page + 1]);
  const visible = [...pages].filter((value) => value >= 1 && value <= totalPages).sort((a, b) => a - b);

  const result: (number | 'gap')[] = [];
  let previous = 0;
  for (const value of visible) {
    if (previous && value - previous > 1) result.push('gap');
    result.push(value);
    previous = value;
  }
  return result;
}

/**
 * Pager that reproduces the design: 28px square buttons, the current page on a
 * #f2f5f8 chip, chevrons on either end.
 */
export function Pagination({ page, totalPages, onChange }: PaginationProps) {
  const pages = pageWindow(page, Math.max(totalPages, 1));

  return (
    <div className="pager" role="navigation" aria-label="Pagination">
      <button
        type="button"
        className="pager__button"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeftIcon size={14} />
      </button>

      {pages.map((entry, index) =>
        entry === 'gap' ? (
          <span key={`gap-${index}`} className="pager__button" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            className={`pager__button ${entry === page ? 'pager__button--active' : ''}`.trim()}
            aria-current={entry === page ? 'page' : undefined}
            onClick={() => onChange(entry)}
          >
            {entry}
          </button>
        ),
      )}

      <button
        type="button"
        className="pager__button"
        aria-label="Next page"
        disabled={page >= Math.max(totalPages, 1)}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRightIcon size={14} />
      </button>
    </div>
  );
}
